// src/oura/components/client/staffScreen.ts
//
// Pieces the two staff screens of this area share (Client, and Search
// performance): the table cell classes, the empty cell, and bringing one row
// of a card into view. Opening a folded card from a link is the kit's
// `useFoldOpener`; the page sentence is ScreenSentence.tsx.

/**
 * Scroll the window so `el`, inside a FoldCard, sits a third of the way down
 * it, once the card has finished unfolding. Returns a cancel, for an effect's
 * cleanup.
 *
 * Opened by a link, a card grows from nothing over its fold transition, and
 * a row scrolled to before that ends lands short: the page is not yet long
 * enough to reach it. And it moves the window only, because the element's
 * own scrollIntoView would also scroll the card's clip box while it grows,
 * leaving the rows shifted inside it.
 */
export function revealInCard(el: HTMLElement, smooth: boolean): () => void {
    // Reading the animations flushes style, so an unfold just committed is
    // already listed. A finished or cancelled one settles either way.
    const unfolding = el.closest("section")?.getAnimations({ subtree: true }).filter((a) => a instanceof CSSTransition) ?? [];
    let live = true;
    void Promise.allSettled(unfolding.map((a) => a.finished)).then(() => {
        if (!live) return;
        const top = el.getBoundingClientRect().top + window.scrollY - window.innerHeight / 3;
        window.scrollTo({ top: Math.max(0, top), behavior: smooth ? "smooth" : "auto" });
    });
    return () => {
        live = false;
    };
}

/** Table cells for a table that runs edge to edge in a flush card: 20px at the card's edges. */
export const TABLE = {
    /**
     * The table's sideways-scroll box. Positioned on purpose: a screen-reader
     * span (sr-only) is absolutely positioned, and with no positioned
     * ancestor inside this box it escapes the box's own clip and stretches
     * the scroll width.
     */
    scroll: "relative overflow-x-auto",
    th: "whitespace-nowrap px-2 py-2.5 text-[13px] font-medium leading-[18px] text-gray-500 first:pl-5 last:pr-5",
    td: "h-12 border-t border-gray-100 px-2 align-middle first:pl-5 last:pr-5",
} as const;

/** The empty table cell. An en dash: a missing value is not a zero. */
export const EMPTY_CELL = "–";
